import { isRecord } from '@/utils/objects';
import type { MessageNode, MessageChannel } from '../types';
import { toNumber, toString } from './parse-helpers';

// Channel detection from event type prefix or message name pattern
const CHANNEL_PATTERNS: Array<{ test: (t: string, n?: string) => boolean; channel: MessageChannel }> = [
  { test: (t) => t.includes('email'), channel: 'email' },
  {
    test: (t, n) =>
      t.includes('twilio') ||
      t.includes('sms') ||
      (n?.startsWith('twilio') ?? false) ||
      (n?.startsWith('sms') ?? false),
    channel: 'sms',
  },
  { test: (t, n) => t.includes('push') || (n?.startsWith('push') ?? false), channel: 'push' },
  {
    test: (t, n) => t.includes('in_app') || (n?.startsWith('in_app') ?? false) || (n?.startsWith('iac') ?? false),
    channel: 'in_app',
  },
  { test: (t, n) => t.includes('inbox') || (n?.startsWith('inbox') ?? false), channel: 'in_app' },
  { test: (t, n) => t.includes('slack') || (n?.startsWith('slack') ?? false), channel: 'slack' },
  { test: (t, n) => t.includes('webhook') || (n?.startsWith('webhook') ?? false), channel: 'webhook' },
  { test: (t, n) => t.includes('whatsapp') || (n?.startsWith('whatsapp') ?? false), channel: 'whatsapp' },
  { test: (t, n) => t.includes('_line') || t.startsWith('line') || (n?.startsWith('line') ?? false), channel: 'line' },
];

export function detectChannel(eventType: string, messageName?: string): MessageChannel {
  for (const { test, channel } of CHANNEL_PATTERNS) {
    if (test(eventType, messageName)) return channel;
  }
  return 'message';
}

const CHANNEL_LABELS: Record<MessageChannel, string> = {
  email: 'email',
  sms: 'SMS',
  push: 'push notification',
  in_app: 'in-app message',
  slack: 'Slack message',
  webhook: 'webhook',
  whatsapp: 'WhatsApp message',
  line: 'LINE message',
  message: 'message',
};

/** How a channel reads in a sentence: `push notification`. */
export function channelLabel(channel: MessageChannel): string {
  return CHANNEL_LABELS[channel];
}

const ACTION_LABELS: Record<string, { normal: string; inverse: string }> = {
  sent: { normal: 'has been sent', inverse: 'has never been sent' },
  delivered: { normal: 'has been delivered', inverse: 'has never been delivered' },
  opened: { normal: 'has been opened', inverse: 'has never been opened' },
  clicked: { normal: 'has been clicked', inverse: 'has never been clicked' },
  bounced: { normal: 'has bounced', inverse: 'has never bounced' },
  converted: { normal: 'has converted', inverse: 'has never converted' },
  failed: { normal: 'has failed to receive', inverse: 'has never failed to receive' },
  dropped: { normal: 'has been suppressed', inverse: 'has never been suppressed' },
  spammed: { normal: 'has been spammed', inverse: 'has never been spammed' },
  unsubscribed: { normal: 'has unsubscribed', inverse: 'has never unsubscribed' },
  replied: { normal: 'has replied', inverse: 'has never replied' },
  undeliverable: { normal: 'has been undeliverable', inverse: 'has never been undeliverable' },
  viewed: { normal: 'has been viewed', inverse: 'has never been viewed' },
};

const DEFAULT_ACTION = { normal: 'has interacted with', inverse: 'has never interacted with' };

function getAction(eventType: string, inverse: boolean): string {
  const prefix = eventType.split('_')[0];
  const labels = ACTION_LABELS[prefix] ?? DEFAULT_ACTION;
  return inverse ? labels.inverse : labels.normal;
}

const CAMPAIGN_ID_PATTERN = /-campaign_(\d+)/;

function isWildcard(name: string | undefined): boolean {
  return name == null || name === '*' || name === '*_h' || name.endsWith('_*');
}

/**
 * Parse message conditions (email, SMS, push, in-app, Slack, webhook, etc.).
 * Handles: { event: { type: 'sent_email' | 'opened_action' | ..., name }, times, within, inverse }
 *          { type: 'sent_email', name, inverse }
 */
export function parseMessage(raw: Record<string, unknown>): MessageNode | null {
  const event = isRecord(raw.event) ? raw.event : undefined;
  const eventType = toString(event?.type ?? event?.ui_type ?? raw.type);
  if (eventType == null) return null;

  // Must contain an action prefix (sent_, opened_, clicked_, etc.)
  const hasActionPrefix =
    eventType.includes('_') && Object.keys(ACTION_LABELS).some((a) => eventType.startsWith(`${a}_`));
  if (!hasActionPrefix) return null;

  const messageName = toString(event?.name ?? raw.name);
  const channel = detectChannel(eventType, messageName);
  const channelLabel = CHANNEL_LABELS[channel];
  // Backend format stores inverse/times/within in event_filter
  const eventFilter = isRecord(raw.event_filter) ? raw.event_filter : undefined;
  const inverse = (eventFilter?.inverse ?? raw.inverse ?? event?.inverse) === true;
  // A decoded named bitmap strips the `_h` suffix and marks the event human instead.
  const humanOnly = messageName?.endsWith('_h') === true || event?.human === true;

  // Build display name
  let displayName: string;
  if (isWildcard(messageName)) {
    displayName = `any ${channelLabel}`;
  } else {
    const enrichedName = toString(event?.message_name ?? raw.message_name);
    displayName = enrichedName != null ? `the ${channelLabel} ${enrichedName}` : `the ${channelLabel} ${messageName}`;
  }

  const action = getAction(eventType, inverse);

  // Extract campaign ID from message name pattern like "email-campaign_126_h"
  const campaignMatch = messageName?.match(CAMPAIGN_ID_PATTERN);
  const campaignId = campaignMatch != null ? toNumber(campaignMatch[1]) : undefined;
  const campaignName = toString(event?.campaign_name ?? raw.campaign_name);

  return {
    type: 'message',
    channel,
    displayName,
    action,
    inverse,
    campaignId,
    campaignName,
    humanOnly,
    times: toNumber(eventFilter?.times ?? raw.times ?? event?.times),
    within: toNumber(eventFilter?.within ?? raw.within ?? event?.within),
    href: toString(raw.href ?? event?.href),
  };
}
